import { useRef, useState } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router';
import { motion } from 'framer-motion';
import { allHeroes, type Hero, type HeroId } from '../../../game/data/heroes';
import { getTribeDefinition } from '../../../game/data/tribes';
import { useMatchStore } from '../../../stores/match';
import { HeroCard } from '../../../components/HeroCard';
import { ScreenPlate } from '../../../components/ScreenPlate';
import { useMotionTier } from '../../../components/useMotionTier';
import { useTransitionStore, measureRect } from '../../../stores/transition';
import { duration, ease, stagger } from '../../../theme/motion';
import { sfx } from '../../../audio/sfx';

// Hero Select — 4 random heroes offered, 1 free reroll per match.
// HS-Battlegrounds-style draft: pick one of four. The 4 IDs live in
// the match store (set on newMatch, swapped by rerollHeroOptions).
// Deep-link guard: if the URL matchId doesn't match the store, kick
// back to /play to start a fresh match.

export function HeroSelectPage() {
  const { matchId: urlMatchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const tier = useMotionTier();
  const reducedMotion = tier === 'reduced';
  const startTransition = useTransitionStore((s) => s.start);
  // Bumped on every reroll so the offered cards re-run their deal animation
  // rather than swapping in place — a reroll should look like a fresh hand.
  const [dealKey, setDealKey] = useState(0);
  const selectedCardRef = useRef<HTMLDivElement | null>(null);
  const storedMatchId = useMatchStore((s) => s.matchId);
  const heroOptions = useMatchStore((s) => s.heroOptions);
  const heroRerollsRemaining = useMatchStore((s) => s.heroRerollsRemaining);
  const selectHero = useMatchStore((s) => s.selectHero);
  const rerollHeroOptions = useMatchStore((s) => s.rerollHeroOptions);

  const [selectedId, setSelectedId] = useState<HeroId | null>(null);

  if (!urlMatchId || storedMatchId !== urlMatchId) {
    return <Navigate to="/play" replace />;
  }

  // Resolve the offered heroes. If the store was reset (mid-match nav)
  // the options array can be empty — guard so the page doesn't blow up.
  const offered: Hero[] = heroOptions
    .map((id) => allHeroes.find((h) => h.id === id))
    .filter((h): h is Hero => !!h);

  const selectedHero = offered.find((h) => h.id === selectedId);

  function confirm() {
    if (!selectedId) return;
    const hero = offered.find((h) => h.id === selectedId);
    const from = measureRect(selectedCardRef.current);

    const go = () => {
      selectHero(selectedId);
      navigate(`/game/${urlMatchId}/draft`);
    };

    // Hand off to the reel advance, carrying the chosen hero's name from the
    // card the player picked to the header that names it on the next step. The
    // run should read as one reel, not four screens.
    const started = startTransition({
      kind: 'advance',
      commit: go,
      slots: from ? { from } : {},
      payload: hero ? { carry: hero.name } : {},
    });
    if (!started) go();
  }

  function reroll() {
    if (heroRerollsRemaining <= 0) return;
    sfx.refresh();
    setSelectedId(null);
    setDealKey((k) => k + 1);
    rerollHeroOptions();
  }

  const canReroll = heroRerollsRemaining > 0;

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      {/* Mural wall — a backdrop you line people up against, which is exactly what
          this screen does. The plate was generated with its middle half left as
          near-empty faded stucco specifically so the four hero cards land in clear
          space and the mural work reads at the edges instead of behind them. */}
      <ScreenPlate src="plates/lineup_wall.webp" scrim={0.74} />

      <div
        aria-hidden
        className="pointer-events-none absolute -left-1/4 -top-1/4 h-[60vw] w-[60vw] max-h-[760px] max-w-[760px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,138,61,0.15) 0%, rgba(255,138,61,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-xs overflow-y-auto px-md py-xs md:gap-md md:px-lg md:py-md lg:max-w-7xl lg:gap-lg lg:px-xl lg:py-lg">
        <header className="flex items-center justify-between">
          <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px] lg:text-[14px]">
            step 1 of 4 · choose your hero
          </p>
          <Link
            to="/play"
            className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← lobby
          </Link>
        </header>

        <motion.div
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={
            reducedMotion
              ? { duration: duration.tap }
              : { duration: duration.enter, ease: ease.out }
          }
        >
          <h1 className="font-cholo text-[20px] leading-none text-sodium sm:text-[24px] md:text-[34px] lg:text-[52px]">
            Pick your headliner
          </h1>
          <p className="mt-xs hidden text-[14px] text-marker md:block md:text-[16px] lg:max-w-3xl lg:text-[18px]">
            Four crews come to the wall tonight. Take one. You've got a free
            reroll if none of them speak to you.
          </p>
        </motion.div>

        {/* 2×2 on portrait phones, 4-across on landscape and up. Portrait
            with 4-col leaves each card ~80px wide which is unreadable. */}
        <div className="grid grid-cols-2 gap-sm sm:grid-cols-4 sm:gap-xs md:gap-md lg:gap-xl">
          {offered.map((hero, i) => (
            <motion.div
              // Keyed on the reroll counter as well as the id so a reroll
              // re-mounts and re-deals instead of silently swapping content.
              key={`${dealKey}-${hero.id}`}
              ref={hero.id === selectedId ? selectedCardRef : undefined}
              initial={
                reducedMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: 18, rotate: i % 2 === 0 ? -3 : 3 }
              }
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={
                reducedMotion
                  ? { duration: duration.tap }
                  : {
                      delay: stagger.base * i,
                      duration: duration.enter,
                      ease: ease.out,
                    }
              }
            >
              <HeroCard
                hero={hero}
                selected={hero.id === selectedId}
                onSelect={() => {
                  sfx.click();
                  setSelectedId(hero.id);
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* Strategy tip — shows the selected hero's tribe lean + first synergy
            breakpoint as a one-liner. Helps newer players see why a hero pairs
            with certain board compositions before locking in. */}
        {selectedHero && <StrategyTip hero={selectedHero} />}

        {/* Confirm + reroll — inline at the bottom for all viewports. */}
        <div className="mt-auto flex items-center justify-between gap-sm md:gap-lg md:pt-md">
          <div className="flex items-center gap-sm md:gap-md">
            <button
              onClick={reroll}
              disabled={!canReroll}
              className={`min-h-[44px] rounded-sm border px-md font-data text-[11px] uppercase tracking-[0.2em] transition-colors md:min-h-[48px] md:px-lg md:text-[13px] lg:text-[14px] ${
                canReroll
                  ? 'border-sodium/40 bg-stucco2 text-sodium hover:bg-stucco3'
                  : 'cursor-not-allowed border-stucco3 bg-stucco1 text-graphite'
              }`}
              style={{ borderColor: canReroll ? 'rgba(255,138,61,0.4)' : undefined }}
            >
              {canReroll ? '↻ Reroll · free' : '↻ Reroll used'}
            </button>
            <p className="hidden font-data text-[12px] uppercase tracking-[0.3em] text-marker md:inline lg:text-[14px]">
              {selectedHero
                ? `selected: ${selectedHero.name}`
                : 'tap a card to preview'}
            </p>
          </div>
          <motion.button
            onClick={confirm}
            disabled={!selectedId}
            whileHover={selectedId && !reducedMotion ? { scale: 1.02 } : undefined}
            whileTap={selectedId && !reducedMotion ? { scale: 0.97 } : undefined}
            className={`inline-block min-h-[44px] rounded-sm px-md py-sm font-data text-[13px] uppercase tracking-[0.2em] transition-colors md:min-h-[52px] md:px-xl md:py-md md:text-[16px] lg:min-h-[64px] lg:px-2xl lg:py-lg lg:text-[20px] ${
              selectedId
                ? 'bg-sodium text-sharpie shadow-sodium hover:bg-sodiumGlow'
                : 'cursor-not-allowed bg-stucco2 text-graphite'
            }`}
          >
            Confirm hero ›
          </motion.button>
        </div>
      </section>
    </main>
  );
}

function StrategyTip({ hero }: { hero: Hero }) {
  const tribe = hero.tribe ? getTribeDefinition(hero.tribe) : null;
  const firstBreakpoint = tribe?.breakpoints[0];
  if (!tribe || !firstBreakpoint) return null;
  const tribeColor = `#${tribe.color.toString(16).padStart(6, '0')}`;
  return (
    <motion.div
      key={hero.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-md border bg-stucco1 p-md md:p-lg"
      style={{ borderColor: `${tribeColor}55` }}
    >
      <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[11px]">
        strategy tip
      </p>
      <p className="mt-xs text-[13px] leading-snug text-bone md:text-[15px]">
        <span className="font-data uppercase tracking-[0.1em]" style={{ color: tribeColor }}>
          {tribe.name}
        </span>{' '}
        — {tribe.description} At 2 units: {firstBreakpoint.description}
      </p>
    </motion.div>
  );
}
